import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
  ElementRef,
  QueryList,
  TemplateRef,
  ViewChildren,
  Renderer2
} from '@angular/core';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, tap, switchMap, finalize } from 'rxjs/operators';
import { SearchResultRowDirective } from '../../directives/search-result-row.directive';
import { ISearchCriteria, AccountRecord, RecordType, HEADER_ROW_HEIGHT } from './account-search.model';
import { CdkConnectedOverlay, ScrollStrategy, ScrollStrategyOptions, ConnectionPositionPair, OriginConnectionPosition, OverlayConnectionPosition, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { TAB, ENTER, ESCAPE, UP_ARROW, DOWN_ARROW } from '@angular/cdk/keycodes';
import { ContentSpinnerComponent } from '@charlesschwab/as-ui-core';
import { AccountSearchService } from './account-search.service';
import { AccountNumberPipe } from '../../pipes/account-number.pipe';
import { MutualFundInvestingService } from '../../../mutual-fund-investing/mutual-fund-investing.service';
import { IntegrationService } from '../../services/integration.service';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HighlightPipe } from '../../pipes/highlight.pipe';

@Component({
    selector: 'account-search',
    templateUrl: './account-search.component.html',
    styleUrls: ['./account-search.component.scss'],
    standalone: true,
    imports: [NgClass, CdkOverlayOrigin, CdkConnectedOverlay, NgStyle, NgTemplateOutlet, SearchResultRowDirective, AccountNumberPipe, HighlightPipe]
})
export class AccountSearchComponent implements OnInit, OnDestroy {
  private searchCriteria$: Subject<ISearchCriteria> = new Subject();
  private subscription: Subscription;
  private clickOutsideFunc: Function;
  private touchendOutsideFunc: Function;
  readonly accountRecordType = RecordType.Account;
  showPanel = false;
  isLoading = false;
  noAccountResults = false;
  statusMessage: '';
  filteredAccounts: Array<AccountRecord> = [];
  searchCriteria: ISearchCriteria;
  keyManager: ActiveDescendantKeyManager<SearchResultRowDirective>;
  scrollStrategy: ScrollStrategy;
  positions: ConnectionPositionPair[];
  @ViewChild(TemplateRef) template: TemplateRef<any>;

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('panel') panel: ElementRef;
  @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
  @ViewChild(ContentSpinnerComponent)
  SchSpinnerComponent?: ContentSpinnerComponent;
  accountNumberPipe = new AccountNumberPipe();
  allFilteredAccounts: Array<AccountRecord> = [];
  public deleteIcon: string;
  @ViewChildren(SearchResultRowDirective) rows: QueryList<SearchResultRowDirective>;

  width = 350;

  panelHeight = 500;

  disabled = false;

  /** The delay in milliseconds between when a keystroke occurs and when a search is performed. */
  delay = 500; // 0.5 sec delay

  @Input()
  selectedValue: string;

  @Input()
  isMasterAccountSearch: boolean;
  @Input()
  isAccountFilter: boolean;
  @Output()
  selected: EventEmitter<AccountRecord> = new EventEmitter();
  minLength = 3;
  @Input()
  searchTerm: string;
  @Output()
  validAccount: EventEmitter<any> = new EventEmitter();
  @Output()
  blankAccountField: EventEmitter<any> = new EventEmitter();
  accountSelected: boolean;
  masterSearchPlaceHolder = 'Firm view (all masters)';
  accountFilterPlaceholder = 'Search';
  @Input()
  viewMode: string;

  constructor(
    private searchService: AccountSearchService,
    private scrollStrategyOptions: ScrollStrategyOptions,
    private renderer: Renderer2,
    private integrationService: IntegrationService,
    private mfInvestingService: MutualFundInvestingService
  ) {
    this.scrollStrategy = this.scrollStrategyOptions.reposition();
    this.setOverlayPosition();
  }

  ngOnInit(): void {
    this.setSearchCriteria();
    this.clickOutsideFunc = this.renderer.listen('document', 'click', (event: any) => this.onDocumentMouseDown(event));
    this.touchendOutsideFunc = this.renderer.listen('document', 'touchend', (event: any) => this.onDocumentMouseDown(event));
    this.search();
    if (this.isMasterAccountSearch) {
      this.minLength = 0;
      this.panelHeight = 300;
      this.width = 366;
      this.searchInput.nativeElement.placeholder = this.masterSearchPlaceHolder;
      this.searchCriteria$.next(this.searchCriteria);
    } else if (this.isAccountFilter) {
      this.panelHeight = 400;
      this.searchInput.nativeElement.placeholder = this.accountFilterPlaceholder;
    }
    const assetUrl = this.integrationService.getApplicationPath();
    this.deleteIcon = assetUrl + 'assets/images/Delete.svg';
  }

  private setOverlayPosition(): void {
    const originPos: OriginConnectionPosition = {
      originX: 'start',
      originY: 'bottom'
    };
    const overlayPos: OverlayConnectionPosition = {
      overlayX: 'start',
      overlayY: 'top'
    };

    const position: ConnectionPositionPair = new ConnectionPositionPair(originPos, overlayPos, 0, 0);
    this.positions = JSON.parse(JSON.stringify([position]));
  }

  private updateOverlayPosition(): void {
    setTimeout(() => {
      if (this.overlay && this.overlay.overlayRef) {
        this.overlay.offsetY = 0;
        this.overlay.overlayRef.updatePosition();
      }
    });
  }

  private search(): void {
    this.subscription = this.searchCriteria$
      .pipe(
        tap(() => {
          this.isLoading = true;
          this.updateOverlayPosition();
          if (!this.isMasterAccountSearch) {
            this.openPanel();
          }
          this.showSpinner();
          this.accountSelected = false;
        })
      )
      .pipe(debounceTime(this.delay))
      .pipe(
        switchMap((criteria) =>
          this.searchService.search(criteria).pipe(
            finalize(() => {
              /* this.isLoading = false; */
            })
          )
        )
      )
      .subscribe(
        (response) => {
          this.isLoading = false;
          if (this.SchSpinnerComponent) {
            this.SchSpinnerComponent.hide();
          }
          this.mfInvestingService.showHideInstructionNotification();
          const unWrappedResponse = response.data.map((r) => {
            return r.attributes;
          });
          this.setFilteredAccounts(unWrappedResponse);
          this.statusMessage = '';
          if (this.checkIFAccountIsExactMatch()) {
            const record: AccountRecord = this.filteredAccounts[0];
            this.select(record);
            return;
          }
          setTimeout(() => {
            this.setActiveItem();
          });
        },
        (error) => {
          this.noAccountResults = true;
          this.filteredAccounts = [];
          this.isLoading = false;
          if (this.SchSpinnerComponent) {
            this.SchSpinnerComponent.hide();
          }
          this.search();
        }
      );
  }

  private setActiveItem(): void {
    this.keyManager = new ActiveDescendantKeyManager<SearchResultRowDirective>(this.rows).withWrap();
    if (this.filteredAccounts.length === 1) {
      this.keyManager.setFirstItemActive();
    } else {
      this.keyManager.setActiveItem(-1);
    }
    if (!this.isMasterAccountSearch) {
      this.searchInput.nativeElement.focus();
    }
    this.updateOverlayPosition();
  }

  private showSpinner(): void {
    setTimeout(() => {
      if (this.SchSpinnerComponent) {
        this.SchSpinnerComponent.show();
      }
    });
  }
  private setSearchCriteria(): void {
    this.searchCriteria = {
      searchTerm: '',
      isMaster: this.isMasterAccountSearch ? true : false
    };
  }

  private setFilteredAccounts(response: any): void {
    this.noAccountResults = !response || response.length === 0;
    this.filteredAccounts = this.noAccountResults ? [] : response;
    this.allFilteredAccounts = this.noAccountResults ? [] : response;
    if (this.isMasterAccountSearch) {
      this.mfInvestingService.masterAccountListUpdated(response);
    }
  }

  ngOnDestroy(): void {
    if (this.clickOutsideFunc) {
      this.clickOutsideFunc();
    }

    if (this.touchendOutsideFunc) {
      this.touchendOutsideFunc();
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  handleFocus(event: any): void {
    if (!this.isMasterAccountSearch) {
      if (event.target.value && event.target.value.length >= this.minLength) {
        if (this.filteredAccounts && this.filteredAccounts.length) {
          this.openPanel();
        }
      } else if (event.target.value && event.target.value.length === 0) {
        this.closePanel(true);
      }
    } else {
      if (event.target.value && event.target.value.length > 3) {
        this.checkIfMasterAccountExists(event.target.value, true);
        this.filteredAccounts = this.allFilteredAccounts;
        this.openPanel();
      } else if (event.target.value.length === 0) {
        this.filteredAccounts = this.allFilteredAccounts;
        this.openPanel();
      }
      this.setActiveItem();
    }
  }

  handleInput(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value: number | string | null = target.value.trim();
    if (!this.isMasterAccountSearch) {
      if (value && value.length >= this.minLength) {
        this.searchCriteria.searchTerm = value;
        this.searchCriteria$.next(this.searchCriteria);
      } else if (value && (value.length > 0 || value.length < this.minLength)) {
        this.clearPanelAndEmitValue();
      } else {
        this.searchCriteria.searchTerm = '';
        this.clearPanelAndEmitValue();
      }
    } else {
      if (value && value.length > 2) {
        this.checkIfMasterAccountExists(value, false);
      } else if (value && value.length > 0) {
        this.closePanel();
      } else {
        this.searchCriteria.searchTerm = '';
        this.filteredAccounts = this.allFilteredAccounts;
        this.openPanel();
      }
    }
  }

  checkIfMasterAccountExists(value, isAccountOnly: boolean): void {
    this.searchCriteria.searchTerm = value;
    let searchStr = value;
    const index = searchStr.indexOf('0');
    if (index === 0) {
      searchStr = searchStr.substr(1, !isAccountOnly ? searchStr.length : 8);
    }
    this.filteredAccounts = this.allFilteredAccounts.filter(
      (account) =>
        (account.accountId.includes(searchStr.replace('-', '')) &&
          this.checkIfFilteredMasterAccountMatchesSearchTerm(searchStr, value, account, index)) ||
        account.accountName.toLowerCase().includes(value.toLowerCase())
    );
    this.openPanel();
  }

  checkIfFilteredMasterAccountMatchesSearchTerm(searchStr, value, account, index): boolean {
    if (index === 0 && account.accountId.indexOf(searchStr) !== 0) {
      if (value.indexOf('-') > -1) {
        const startIndex = value.indexOf('0');
        let updateValue;
        if (startIndex === 0) {
          updateValue = searchStr.substr(1, value.length);
        }
        return account.accountId.indexOf(updateValue.replace('-', '')) > -1;
      } else {
        return account.accountId.indexOf(value) > -1;
      }
    }
    return true;
  }

  handleKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    const isArrowKey = keyCode === UP_ARROW || keyCode === DOWN_ARROW;

    if (this.showPanel && (isArrowKey || keyCode === ESCAPE || keyCode === ENTER || keyCode === TAB)) {
      if (keyCode === ESCAPE || keyCode === TAB) {
        this.closePanel();
        event.preventDefault();
      } else if (isArrowKey) {
        const prevActiveItem = this.keyManager.activeItem;
        this.keyManager.onKeydown(event);
        if (isArrowKey || this.keyManager.activeItem !== prevActiveItem) {
          this.scrollToOption();
        }
      } else if (this.keyManager) {
        if (keyCode === ENTER) {
          if (this.keyManager.activeItem) {
            this.keyManager.activeItem.selectViaInteraction();
          } else if (this.isAccountFilter && !this.keyManager.activeItem && this.filteredAccounts.length > 0) {
            this.select(this.filteredAccounts[0]);
          }
          event.preventDefault();
        }
      }
    }
  }

  clearSearch(): void {
    this.filteredAccounts = [];
    this.showPanel = false;
  }

  select(record: AccountRecord): void {
    const transformedAccountNumber = this.accountNumberPipe.transform(record.accountId);
    if (this.isAccountFilter) {
      this.searchInput.nativeElement.value = transformedAccountNumber;
    } else if (!this.isMasterAccountSearch) {
      this.searchInput.nativeElement.value = transformedAccountNumber + ' ' + record.accountName + ' - ' + record.type;
    } else {
      this.searchInput.nativeElement.value = transformedAccountNumber + ' ' + record.accountName;
    }
    this.clearSearch();
    this.selected.emit(record);
    this.accountSelected = true;
  }

  private onDocumentMouseDown = (event: any) => {
    const clickTarget = event.target as HTMLElement;
    if (
      this.overlay &&
      this.showPanel &&
      clickTarget !== this.searchInput.nativeElement &&
      !clickTarget.className.includes('deleteIcon') &&
      !this.overlay.overlayRef.overlayElement.contains(clickTarget) &&
      event.target &&
      event.target.tagName !== 'BODY'
    ) {
      this.closePanel();
    }
  };

  private openPanel(): void {
    this.showPanel = true;
  }

  clearPanelAndEmitValue(): void {
    this.showPanel = false;
    this.filteredAccounts = [];
    this.selected.emit(null);
  }

  closePanel(clearResults?: boolean): void {
    if (clearResults) {
      this.clearResults();
    }
    this.showPanel = false;
    if (!this.selectedValue) {
      if (this.checkIFAccountIsExactMatch()) {
        const inputValue = this.searchInput.nativeElement.value.replace('-', '');
        const accountData = this.allFilteredAccounts.find((item) => item.accountId === inputValue);
        if (!this.isMasterAccountSearch) {
          this.selected.emit({
            accountId: accountData.accountId,
            accountName: accountData.accountName,
            type: accountData.type
          });
        } else {
          this.selected.emit({ accountId: accountData.accountId, accountName: accountData.accountName });
        }
      }
    }
  }

  private clearResults(): void {
    this.filteredAccounts = [];
    this.keyManager = null;
    this.setSearchCriteria();
    this.searchInput.nativeElement.value = '';
  }

  private scrollToOption(): void {
    if (this.panel) {
      const parentElement = this.panel.nativeElement;
      const rowActive = parentElement.querySelector('.row-active');

      if (rowActive) {
        rowActive.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
      if (this.keyManager.activeItemIndex === 0) {
        this.panel.nativeElement.scrollTop = 0;
      }
    }
  }

  private getRowScrollPosition(
    rowIndex: number,
    rowHeight: number,
    currentScrollPosition: number,
    panelHeight: number
  ): number {
    const rowOffset = rowIndex > 0 ? rowIndex * rowHeight + HEADER_ROW_HEIGHT : 0;

    if (rowOffset < currentScrollPosition) {
      return rowOffset;
    }

    if (rowOffset + rowHeight > currentScrollPosition + panelHeight) {
      return Math.max(0, rowOffset - panelHeight + rowHeight);
    }

    return currentScrollPosition;
  }

  clearAccount(): void {
    if (this.isMasterAccountSearch) {
      this.searchInput.nativeElement.placeholder = this.masterSearchPlaceHolder;
      this.setSearchCriteria();
      this.searchInput.nativeElement.value = '';
      this.selectedValue = '';
      this.closePanel(false);
      this.accountSelected = false;
      this.blankAccountField.emit(null);
    } else {
      this.selectedValue = '';
      this.closePanel(true);
      this.selected.emit(null);
      this.accountSelected = false;
      this.blankAccountField.emit(true);
    }
  }

  checkIfAccountIsBlank(): void {
    if (!this.isMasterAccountSearch) {
      if (this.searchInput.nativeElement.value === '' && this.searchInput.nativeElement.value.length === 0) {
        this.blankAccountField.emit(true);
      } else if (
        !this.accountSelected &&
        this.searchInput.nativeElement.value.length >= 3 &&
        this.allFilteredAccounts &&
        this.allFilteredAccounts.length <= 0
      ) {
        this.validAccount.emit(false);
      } else if (!this.accountSelected || this.allFilteredAccounts.length === 0) {
        let inputValue = this.searchInput.nativeElement.value.replace('-', '');
        if (inputValue) {
          inputValue = inputValue.substr(0, inputValue.length);
        }
        this.checkIfAccountExistsInAvailableFunds(inputValue);
      }
    } else {
      const inputValue = this.searchInput.nativeElement.value.replace('-', '');
      if (inputValue && inputValue.match(/^\d+$/) && (inputValue.length === 8 || inputValue.length === 9)) {
        let searchStr = inputValue;
        const index = inputValue.indexOf('0');
        if (index === 0) {
          searchStr = searchStr.substr(1, searchStr.length);
        }
        const account: AccountRecord = this.allFilteredAccounts.find((item) => searchStr === item.accountId);
        if (account) {
          this.select(account);
        }
      }
    }
  }

  private checkIfAccountExistsInAvailableFunds(symbol: string): void {
    const accountData = this.allFilteredAccounts.find((item) => item.accountId === symbol);
    if (!accountData) {
      this.validAccount.emit(false);
    } else {
      this.validAccount.emit(true);
    }
  }

  checkIFAccountIsExactMatch(): boolean {
    const inputValue = this.searchCriteria.searchTerm.replace('-', '');
    if (inputValue && inputValue.match(/^\d+$/) && (inputValue.length === 8 || inputValue.length === 9) && this.allFilteredAccounts.length === 1) {
      return true;
    }
    return false;
  }
}
